import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';
import { format, addMonths, addDays, differenceInMonths } from 'date-fns';
import type { PeriodType } from '@/types/financial';

// ---------------------------------------------------------------------------
// Pure helpers — exported for direct unit testing
// ---------------------------------------------------------------------------

/**
 * AU Financial Year End dropdown option.
 * Each option represents a 30 June date for a given year.
 */
export interface AuFyEndOption {
  /** Display text, e.g. "30 June 2026" */
  label: string;
  /** ISO date string, e.g. "2026-06-30" */
  value: string;
  /** Corresponding Date object */
  date: Date;
}

/**
 * Generate dropdown options for AU financial year ends (30 June × N years).
 * Returns options for referenceYear ± 6 years, sorted oldest to newest.
 */
export function generateAuFyEndOptions(referenceYear: number): AuFyEndOption[] {
  const options: AuFyEndOption[] = [];
  for (let year = referenceYear - 6; year <= referenceYear + 6; year++) {
    const date = new Date(year, 5, 30); // June = 5 (0-indexed)
    options.push({
      label: `30 June ${year}`,
      value: `${year}-06-30`,
      date,
    });
  }
  return options;
}

/**
 * Derive period label from periodType and endDate.
 *
 * - historical  → "FY{endDate.year}"
 * - ytd         → "YTD {Mon} {year}" (based on endDate)
 * - forecast    → "FY{endDate.year} (Forecast)"
 */
export function derivePeriodLabel(periodType: PeriodType, endDate: Date): string {
  if (periodType === 'historical') {
    return `FY${endDate.getFullYear()}`;
  }
  if (periodType === 'ytd') {
    return `YTD ${format(endDate, 'MMM yyyy')}`;
  }
  if (periodType === 'forecast') {
    return `FY${endDate.getFullYear()} (Forecast)`;
  }
  return '';
}

/**
 * Calculate start/end dates for a given period type and FY End date.
 *
 * The key insight: "start of a financial year" = day after the *previous* FY End.
 *   Previous FY End = addMonths(fyEnd, -12)
 *   Start           = addDays(previousFyEnd, 1)
 *
 * - historical: previous full FY (start of that FY → fyEnd)
 * - ytd:        current FY start → today
 * - forecast:   next FY (day after fyEnd → fyEnd + 12 months)
 */
export function calculatePeriodDates(
  periodType: PeriodType,
  fyEnd: Date,
): { startDate: Date; endDate: Date } {
  if (periodType === 'historical') {
    // Previous FY End is exactly 12 months before the given FY End
    const previousFyEnd = addMonths(fyEnd, -12);
    const startDate = addDays(previousFyEnd, 1);
    return { startDate, endDate: fyEnd };
  }

  if (periodType === 'ytd') {
    // Current FY started the day after the previous FY End (12 months ago)
    const previousFyEnd = addMonths(fyEnd, -12);
    const startDate = addDays(previousFyEnd, 1);
    const endDate = new Date();
    return { startDate, endDate };
  }

  if (periodType === 'forecast') {
    // Next FY: starts day after this FY End, ends 12 months after this FY End
    const startDate = addDays(fyEnd, 1);
    const endDate = addMonths(fyEnd, 12);
    return { startDate, endDate };
  }

  // Fallback — should not be reached with valid PeriodType
  return { startDate: fyEnd, endDate: fyEnd };
}

export interface WizardStepPeriodProps {
  financialYearEnd?: Date;
  periodType?: PeriodType;
  periodLabel?: string;
  startDate?: Date;
  endDate?: Date;
  monthsCovered?: number;
  annualisationFactor?: number;
  onChange: (updates: {
    financialYearEnd?: Date;
    periodType?: PeriodType;
    periodLabel?: string;
    startDate?: Date;
    endDate?: Date;
    monthsCovered?: number;
    annualisationFactor?: number;
  }) => void;
  onNext: () => void;
  onBack: () => void;
  onCancel: () => void;
}

/**
 * Wizard Step 3: Period Configuration
 *
 * Fields:
 * - Financial Year End (date picker) - auto-inferred from entity if set
 * - Period Type (Full Year / YTD / Interim / Forecast)
 * - Period Label (auto-generated, editable)
 * - Start Date (auto-calculated)
 * - End Date (auto-calculated)
 * - Months Covered (auto-calculated, user can override)
 * - Annualisation Factor (auto-shown for YTD/Interim)
 */
export const WizardStepPeriod: React.FC<WizardStepPeriodProps> = ({
  financialYearEnd,
  periodType,
  periodLabel,
  startDate,
  endDate,
  monthsCovered,
  annualisationFactor,
  onChange,
  onNext,
  onBack,
  onCancel,
}) => {
  // Local state for date inputs (as strings for native inputs)
  const [fyEndStr, setFyEndStr] = useState(
    financialYearEnd ? format(financialYearEnd, 'yyyy-MM-dd') : ''
  );
  const [startDateStr, setStartDateStr] = useState(
    startDate ? format(startDate, 'yyyy-MM-dd') : ''
  );
  const [endDateStr, setEndDateStr] = useState(
    endDate ? format(endDate, 'yyyy-MM-dd') : ''
  );

  // Track whether user has manually edited the label
  const [isLabelUserEdited, setIsLabelUserEdited] = useState(false);

  // AU FY dropdown: show custom date picker only when toggled
  const currentYear = new Date().getFullYear();
  const auFyOptions = generateAuFyEndOptions(currentYear);
  const isStandardAuFy = auFyOptions.some((opt) => opt.value === fyEndStr);
  const [useCustomDate, setUseCustomDate] = useState(
    fyEndStr !== '' && !isStandardAuFy
  );

  // Auto-calculate dates and annualisation when period type or FY end changes
  useEffect(() => {
    if (!periodType || !fyEndStr) return;

    const fyEnd = new Date(fyEndStr);
    const { startDate: calculatedStart, endDate: calculatedEnd } =
      calculatePeriodDates(periodType, fyEnd);

    const label = derivePeriodLabel(periodType, calculatedEnd);

    setStartDateStr(format(calculatedStart, 'yyyy-MM-dd'));
    setEndDateStr(format(calculatedEnd, 'yyyy-MM-dd'));
    // Reset user-edited flag when period type or FY end changes
    setIsLabelUserEdited(false);

    const months = differenceInMonths(calculatedEnd, calculatedStart) + 1;
    const factor = periodType === 'ytd' || periodType === 'forecast' ? 12 / months : undefined;

    onChange({
      financialYearEnd: fyEnd,
      periodType,
      periodLabel: label,
      startDate: calculatedStart,
      endDate: calculatedEnd,
      monthsCovered: months,
      annualisationFactor: factor,
    });
  }, [periodType, fyEndStr]);

  // Manual date changes
  const handleStartDateChange = (value: string) => {
    setStartDateStr(value);
    const newStart = new Date(value);
    const end = endDateStr ? new Date(endDateStr) : undefined;

    if (end) {
      const months = differenceInMonths(end, newStart) + 1;
      const factor =
        periodType === 'ytd' || periodType === 'forecast' ? 12 / months : undefined;

      onChange({
        startDate: newStart,
        monthsCovered: months,
        annualisationFactor: factor,
      });
    }
  };

  const handleEndDateChange = (value: string) => {
    setEndDateStr(value);
    const newEnd = new Date(value);
    const start = startDateStr ? new Date(startDateStr) : undefined;

    if (start) {
      const months = differenceInMonths(newEnd, start) + 1;
      const factor =
        periodType === 'ytd' || periodType === 'forecast' ? 12 / months : undefined;

      // Re-derive label unless user has manually edited it
      const labelUpdate =
        !isLabelUserEdited && periodType
          ? { periodLabel: derivePeriodLabel(periodType, newEnd) }
          : {};

      onChange({
        endDate: newEnd,
        monthsCovered: months,
        annualisationFactor: factor,
        ...labelUpdate,
      });
    }
  };

  const isNextDisabled =
    !periodType || !periodLabel || !startDateStr || !endDateStr || !monthsCovered;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold" data-testid="wizard-step-title">
          Period Configuration
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Configure the financial period details
        </p>
      </div>

      <div className="space-y-4">
        {/* Financial Year End */}
        <div className="space-y-2">
          <Label htmlFor="fy-end">Financial Year End</Label>
          {!useCustomDate ? (
            <>
              <Select
                value={fyEndStr || 'none'}
                onValueChange={(value) => {
                  if (value !== 'none') {
                    setFyEndStr(value);
                  }
                }}
              >
                <SelectTrigger id="fy-end" data-testid="select-financial-year-end">
                  <SelectValue placeholder="Select financial year end" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" disabled>
                    Select financial year end
                  </SelectItem>
                  {auFyOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                type="button"
                className="text-xs text-muted-foreground underline hover:text-foreground"
                data-testid="button-use-custom-date"
                onClick={() => setUseCustomDate(true)}
              >
                Use custom date
              </button>
            </>
          ) : (
            <>
              <input
                type="date"
                id="fy-end"
                data-testid="input-financial-year-end"
                value={fyEndStr}
                onChange={(e) => setFyEndStr(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
              <button
                type="button"
                className="text-xs text-muted-foreground underline hover:text-foreground"
                data-testid="button-use-standard-fy"
                onClick={() => setUseCustomDate(false)}
              >
                Use standard 30 June
              </button>
            </>
          )}
          <p className="text-xs text-muted-foreground">
            {useCustomDate
              ? 'Enter a custom financial year end date'
              : 'Australian financial year ends 30 June'}
          </p>
        </div>

        {/* Period Type */}
        <div className="space-y-2">
          <Label htmlFor="period-type">Period Type</Label>
          <Select
            value={periodType || 'none'}
            onValueChange={(value) => onChange({ periodType: value as PeriodType })}
          >
            <SelectTrigger id="period-type" data-testid="select-period-type">
              <SelectValue placeholder="Select period type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none" disabled>
                Select period type
              </SelectItem>
              <SelectItem value="historical">Full Year (Historical)</SelectItem>
              <SelectItem value="ytd">Year to Date (YTD)</SelectItem>
              <SelectItem value="forecast">Forecast</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Period Label */}
        <div className="space-y-2">
          <Label htmlFor="period-label">Period Label</Label>
          <input
            type="text"
            id="period-label"
            data-testid="input-period-label"
            value={periodLabel || ''}
            onChange={(e) => {
              setIsLabelUserEdited(true);
              onChange({ periodLabel: e.target.value });
            }}
            placeholder="e.g., FY2024, Q1 2024"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        {/* Start Date */}
        <div className="space-y-2">
          <Label htmlFor="start-date">Start Date</Label>
          <input
            type="date"
            id="start-date"
            data-testid="input-period-start-date"
            value={startDateStr}
            onChange={(e) => handleStartDateChange(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        {/* End Date */}
        <div className="space-y-2">
          <Label htmlFor="end-date">End Date</Label>
          <input
            type="date"
            id="end-date"
            data-testid="input-period-end-date"
            value={endDateStr}
            onChange={(e) => handleEndDateChange(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        {/* Months Covered (read-only display) */}
        {monthsCovered && (
          <div className="space-y-2">
            <Label>Period Duration</Label>
            <div
              className="flex h-10 items-center rounded-md border border-input bg-muted px-3 py-2 text-sm"
              data-testid="text-months-covered"
            >
              {monthsCovered} months
            </div>
          </div>
        )}

        {/* Annualisation Factor (for YTD/Forecast) */}
        {annualisationFactor && (periodType === 'ytd' || periodType === 'forecast') && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <div className="flex justify-between items-center">
                <span>Annualisation Factor:</span>
                <span
                  className="font-semibold"
                  data-testid="text-annualisation-factor"
                >
                  {annualisationFactor.toFixed(2)}x
                </span>
              </div>
              <p className="text-xs mt-1">
                Multiply {monthsCovered}-month figures by {annualisationFactor.toFixed(2)} to
                estimate full-year values
              </p>
            </AlertDescription>
          </Alert>
        )}
      </div>

      <div className="flex justify-between pt-4">
        <div className="flex gap-2">
          <Button variant="outline" onClick={onBack} data-testid="button-wizard-back">
            Back
          </Button>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
        <Button onClick={onNext} disabled={isNextDisabled} data-testid="button-wizard-next">
          Next
        </Button>
      </div>
    </div>
  );
};
